import { useSearchParams } from '@solidjs/router';
import { createMemo } from 'solid-js';

// URL-backed filter state. Filters on /stock and /strains live in the
// query string so that:
//
//   1. Navigating to a strain detail and clicking Back restores the
//      exact filter state via the router (no extra plumbing).
//   2. Users can copy/share a filtered URL and the recipient sees the
//      same view.
//   3. SSR can read filters off the request and render the correct
//      filtered HTML for crawlers.
//
// All setters use `replace: true`. Without it, every keystroke in the
// search input would push a new history entry and the back button
// would have to walk through dozens of intermediate states before
// leaving the page. With it, only navigation (clicking a strain card,
// returning from detail) creates history entries; filter mutations
// just rewrite the current URL.

type SetSearchOpts = { replace?: boolean };
type SearchParamValue = string | string[] | undefined;

// The Solid router's setParams accepts a partial record; values set
// to undefined remove the corresponding param from the URL. We exploit
// that for "no filter applied" so empty/default state produces a
// clean URL with no leftover keys.
type SetSearchFn = (
  params: Record<string, SearchParamValue>,
  opts?: SetSearchOpts,
) => void;

// One shared call to useSearchParams per route gives us both the
// reactive accessor and the setter. Helpers below close over `params`
// and `setParams`. Each helper returns a `[get, set]` tuple that
// matches the createSignal shape so callers can swap signals for URL
// state with minimal JSX churn.
export function urlFilters() {
  const [params, setParams] = useSearchParams();
  const set: SetSearchFn = (next, opts) => setParams(next, { replace: true, ...opts });
  return { params, set };
}

/**
 * String accessor — empty string is the absent state. Useful for the
 * search box: typing clears when string is empty (param removed),
 * setting any non-empty value writes to the URL.
 */
export function urlString(
  params: ReturnType<typeof useSearchParams>[0],
  set: SetSearchFn,
  key: string,
): [() => string, (v: string) => void] {
  return [
    () => readString(params[key]),
    (v: string) => set({ [key]: v ? v : undefined }),
  ];
}

/**
 * Boolean accessor with a configurable default (so /strains can have
 * "in stock" default off and /stock can have it default on without
 * leaking the difference into URL noise). When the value matches the
 * default we omit the param entirely.
 */
export function urlBool(
  params: ReturnType<typeof useSearchParams>[0],
  set: SetSearchFn,
  key: string,
  defaultValue: boolean,
): [() => boolean, (v: boolean) => void] {
  return [
    () => {
      const raw = readString(params[key]);
      if (raw === '') return defaultValue;
      return raw === '1' || raw.toLowerCase() === 'true';
    },
    (v: boolean) => set({ [key]: v === defaultValue ? undefined : v ? '1' : '0' }),
  ];
}

/**
 * Nullable-number accessor. Renders as `?key=15`. Null clears the
 * param; reading a non-numeric value returns null defensively (so a
 * user editing the URL by hand can't crash the route).
 */
export function urlNumber(
  params: ReturnType<typeof useSearchParams>[0],
  set: SetSearchFn,
  key: string,
): [() => number | null, (v: number | null) => void] {
  return [
    () => {
      const raw = readString(params[key]);
      if (raw === '') return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    },
    (v: number | null) => set({ [key]: v == null ? undefined : String(v) }),
  ];
}

/**
 * Set<string> accessor for multi-select filters (categories, lineages,
 * brands, irradiations). Comma-separated in the URL, decoded
 * defensively — empty strings between commas are dropped, duplicates
 * are deduped.
 *
 * The optional `validate` predicate filters parsed values down to the
 * known set, so an attacker pasting `?cat=DROP%20TABLE` doesn't make
 * it through to the filter logic. Callers pass a tight allowlist
 * (e.g. CATEGORY_OPTIONS) for enum-shaped filters; brand-style
 * free-text sets pass `() => true`.
 */
export function urlSet<T extends string>(
  params: ReturnType<typeof useSearchParams>[0],
  set: SetSearchFn,
  key: string,
  validate: (v: string) => v is T,
): [() => Set<T>, (next: Set<T>) => void] {
  // Memoised so consumers calling the getter multiple times in the
  // same render get the same Set instance — important for the
  // matchesFilters() pattern where each filter dimension is read
  // twice (once for "in stock" check, once for "is the option in
  // the active set" check) per strain.
  const value = createMemo(() => {
    const raw = readString(params[key]);
    if (raw === '') return new Set<T>();
    const out = new Set<T>();
    for (const part of raw.split(',')) {
      const trimmed = part.trim();
      if (trimmed && validate(trimmed)) out.add(trimmed);
    }
    return out;
  });
  return [
    value,
    (next: Set<T>) => set({ [key]: next.size ? [...next].join(',') : undefined }),
  ];
}

/**
 * String-set accessor that accepts arbitrary values. For brand
 * filters where the allowed set isn't known statically — we still
 * trim and dedupe but don't validate the shape.
 */
export function urlStringSet(
  params: ReturnType<typeof useSearchParams>[0],
  set: SetSearchFn,
  key: string,
): [() => Set<string>, (next: Set<string>) => void] {
  const value = createMemo(() => {
    const raw = readString(params[key]);
    if (raw === '') return new Set<string>();
    const out = new Set<string>();
    for (const part of raw.split(',')) {
      const trimmed = part.trim();
      if (trimmed) out.add(trimmed);
    }
    return out;
  });
  return [
    value,
    (next: Set<string>) => set({ [key]: next.size ? [...next].join(',') : undefined }),
  ];
}

// SearchParams values can be string | string[] | undefined when the
// same key appears more than once. We always treat them as scalar
// strings — the multi-select serialisation uses comma-separation, not
// repeated keys, so a `string[]` here is a degenerate case from a
// hand-crafted URL. Take the first value to keep behaviour
// deterministic.
function readString(v: SearchParamValue): string {
  if (v == null) return '';
  if (Array.isArray(v)) return v[0] ?? '';
  return v;
}
